import { useState } from 'react';
import { Check, Copy, Share2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';
import {
	buildWhatsAppPatientSessionReminderUrl,
	normalizeWhatsAppPhoneNumber,
} from '@/constants/whatsapp';

type PublicSessionAccessLinkFieldProps = {
	url: string;
	patientName?: string | null;
	phone?: string | null;
	/** Alinha com as linhas do modal de sessão do profissional. */
	variant?: 'default' | 'session-modal';
};

const actionButtonClassName =
	'flex shrink-0 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-50';

const PublicSessionAccessLinkField = ({
	url,
	patientName,
	phone,
	variant = 'default',
}: PublicSessionAccessLinkFieldProps) => {
	const [copied, setCopied] = useState(false);
	const hasPatientPhone = normalizeWhatsAppPhoneNumber(phone) != null;

	const handleWhatsAppShare = () => {
		const whatsappUrl = buildWhatsAppPatientSessionReminderUrl({
			url,
			patientName,
			phone,
		});
		window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
	};

	const handleCopySessionLink = async () => {
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			toast.success('Link da sessão copiado');
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error('Não foi possível copiar o link');
		}
	};

	const copyLinkButton = (
		<button
			type="button"
			onClick={handleCopySessionLink}
			className={actionButtonClassName}
			title="Copiar link de acesso da sessão"
		>
			{copied ? (
				<Check
					size={15}
					strokeWidth={2.25}
					className="text-emerald-600"
				/>
			) : (
				<Copy size={15} strokeWidth={2} />
			)}
			<span>Copiar link da sessão</span>
		</button>
	);

	const whatsAppButton = hasPatientPhone ? (
		<button
			type="button"
			onClick={handleWhatsAppShare}
			className={actionButtonClassName}
			title="Enviar lembrete no WhatsApp do paciente"
		>
			<Share2 size={15} strokeWidth={2} />
			<span>Enviar lembrete para WhatsApp</span>
		</button>
	) : null;

	if (variant === 'session-modal') {
		return (
			<>
				{whatsAppButton}
				{copyLinkButton}
			</>
		);
	}

	return (
		<div className={cn('flex flex-wrap items-center gap-2')}>
			{whatsAppButton}
			{copyLinkButton}
		</div>
	);
};

export { PublicSessionAccessLinkField };
